import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { Badge } from '../../components/ui/Badge';
import { Plus, Search, Edit, Trash2, X, Key, Shield, Filter, CheckCircle, AlertCircle } from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';
import DataTable from '../../components/ui/DataTable';
import toast from 'react-hot-toast';

const PermissionManagement = () => {
  const { getMockData, updateMockData } = useDataContext();
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [newPermission, setNewPermission] = useState({
    permissionName: '',
    permissionCode: '',
    resourceType: '',
    action: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPermissions();
  }, [getMockData]);

  const loadPermissions = () => {
    const permissionsData = getMockData('permissions');
    if (!permissionsData || !permissionsData.permissions) {
      // Initialize with default permissions if none exist
      const defaultPermissions = [
        {
          id: 1,
          permissionName: 'View Users',
          permissionCode: 'USERS_VIEW',
          resourceType: 'users',
          action: 'read',
          description: 'View user list and details',
          category: 'user_management'
        },
        {
          id: 2,
          permissionName: 'Create Users',
          permissionCode: 'USERS_CREATE',
          resourceType: 'users',
          action: 'create',
          description: 'Create new users',
          category: 'user_management'
        },
        {
          id: 3,
          permissionName: 'Edit Users',
          permissionCode: 'USERS_EDIT',
          resourceType: 'users',
          action: 'update',
          description: 'Edit existing users',
          category: 'user_management'
        },
        {
          id: 4,
          permissionName: 'Delete Users',
          permissionCode: 'USERS_DELETE',
          resourceType: 'users',
          action: 'delete',
          description: 'Delete users',
          category: 'user_management'
        },
        {
          id: 5,
          permissionName: 'Manage Roles',
          permissionCode: 'ROLES_MANAGE',
          resourceType: 'roles',
          action: 'manage',
          description: 'Manage system roles',
          category: 'role_management'
        },
        {
          id: 6,
          permissionName: 'Manage Permissions',
          permissionCode: 'PERMISSIONS_MANAGE',
          resourceType: 'permissions',
          action: 'manage',
          description: 'Manage system permissions',
          category: 'role_management'
        }
      ];
      setPermissions(defaultPermissions);
      updateMockData('permissions', { permissions: defaultPermissions });
    } else {
      setPermissions(permissionsData.permissions);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!newPermission.permissionName.trim()) {
      newErrors.permissionName = 'Permission name is required';
    }

    if (!newPermission.permissionCode.trim()) {
      newErrors.permissionCode = 'Permission code is required';
    } else if (!/^[A-Z0-9_]+$/.test(newPermission.permissionCode)) {
      newErrors.permissionCode = 'Code must be uppercase letters, numbers, and underscores only';
    }

    if (!newPermission.resourceType.trim()) {
      newErrors.resourceType = 'Resource type is required';
    }

    if (!newPermission.action.trim()) {
      newErrors.action = 'Action is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreatePermission = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Check if permission code already exists
      const existingPermission = permissions.find(
        p => p.permissionCode.toLowerCase() === newPermission.permissionCode.toLowerCase()
      );
      if (existingPermission) {
        setErrors({ permissionCode: 'Permission code already exists' });
        setLoading(false);
        return;
      }

      const permission = {
        id: Date.now(),
        ...newPermission,
        category: newPermission.resourceType || 'general',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const permissionsData = getMockData('permissions') || { permissions: [] };
      const updatedPermissions = [...permissionsData.permissions, permission];
      updateMockData('permissions', { permissions: updatedPermissions });
      
      setPermissions([...permissions, permission]);
      
      toast.success(`Permission "${newPermission.permissionName}" created successfully`);
      
      setNewPermission({
        permissionName: '',
        permissionCode: '',
        resourceType: '',
        action: '',
        description: ''
      });
      setErrors({});
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating permission:', error);
      toast.error('Failed to create permission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermission = (permission) => {
    setSelectedPermission(permission);
    setNewPermission({
      permissionName: permission.permissionName || '',
      permissionCode: permission.permissionCode || '',
      resourceType: permission.resourceType || '',
      action: permission.action || '',
      description: permission.description || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdatePermission = () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    // Check if permission code already exists (excluding current permission)
    const existingPermission = permissions.find(
      p => p.permissionCode.toLowerCase() === newPermission.permissionCode.toLowerCase() &&
           p.id !== selectedPermission.id
    );
    if (existingPermission) {
      setErrors({ permissionCode: 'Permission code already exists' });
      return;
    }

    const permissionsData = getMockData('permissions') || { permissions: [] };
    const updatedPermissions = permissionsData.permissions.map(p =>
      p.id === selectedPermission.id
        ? { 
            ...p, 
            ...newPermission,
            category: newPermission.resourceType || p.category,
            updatedAt: new Date().toISOString() 
          }
        : p
    );
    updateMockData('permissions', { permissions: updatedPermissions });
    
    setPermissions(permissions.map(p => 
      p.id === selectedPermission.id 
        ? { ...p, ...newPermission, category: newPermission.resourceType || p.category }
        : p
    ));
    
    setIsEditModalOpen(false);
    setSelectedPermission(null);
    toast.success('Permission updated successfully');
  };

  const handleDeletePermission = (permissionId) => {
    const permission = permissions.find(p => p.id === permissionId);
    
    if (!window.confirm(
      `⚠️ Are you sure you want to delete "${permission?.permissionName || 'this permission'}"?\n\n` +
      `This action cannot be undone.\n\n` +
      `Permission: ${permission?.permissionName}\n` +
      `Code: ${permission?.permissionCode}`
    )) {
      return;
    }
    
    const permissionsData = getMockData('permissions') || { permissions: [] };
    const updatedPermissions = permissionsData.permissions.filter(p => p.id !== permissionId);
    updateMockData('permissions', { permissions: updatedPermissions });
    
    setPermissions(permissions.filter(p => p.id !== permissionId));
    
    toast.success(`Permission "${permission?.permissionName}" has been deleted.`);
  };

  const filteredPermissions = (permissions || []).filter(permission => {
    const matchesSearch = permission?.permissionName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission?.permissionCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         permission?.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || permission?.category === categoryFilter ||
                           permission?.resourceType === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(permissions.map(p => p.category || p.resourceType).filter(Boolean))];

  const columns = [
    {
      key: 'permissionName',
      label: 'Permission',
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <Key className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <div className="font-semibold">{row.permissionName}</div>
            <div className="text-sm text-gray-500">{row.permissionCode}</div>
          </div>
        </div>
      )
    },
    {
      key: 'resourceType',
      label: 'Resource Type',
      render: (value) => (
        <Badge variant="outline">
          {value || 'N/A'}
        </Badge>
      )
    },
    {
      key: 'action',
      label: 'Action',
      render: (value) => (
        <Badge variant="secondary">
          {value || 'N/A'}
        </Badge>
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (value) => (
        <span className="text-sm text-gray-600">{value || 'No description'}</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleEditPermission(row)}
            title="Edit Permission"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleDeletePermission(row.id)}
            className="text-red-600 hover:text-red-700"
            title="Delete Permission"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg p-6 sm:p-8 lg:p-12 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <Badge className="mb-4 bg-white/20 text-white border-white/30">
            Role-Based Access Control
          </Badge>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2">
            Permission Management
          </h1>
          <p className="text-white/90 text-sm sm:text-base">
            Manage system permissions for role-based access control (RBAC)
          </p>
        </div>
        <div className="absolute bottom-4 right-4 sm:right-6 lg:right-12">
          <Button 
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-white text-purple-600 hover:bg-gray-100"
            size="lg"
          >
            <Plus className="mr-2 h-5 w-5" />
            Create Permission
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            placeholder="Search permissions by name, code, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary */}
      <div className="text-sm text-gray-600">
        Showing <strong>{filteredPermissions.length}</strong> of <strong>{permissions.length}</strong> permissions
      </div>

      {/* Permissions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            All Permissions
          </CardTitle>
          <CardDescription>
            {permissions.length} permission{permissions.length !== 1 ? 's' : ''} defined in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={filteredPermissions}
            columns={columns}
            emptyMessage="No permissions found. Create your first permission to get started."
          />
        </CardContent>
      </Card>

      {/* Create Permission Modal */}
      {isCreateModalOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => {
              if (!loading) {
                setIsCreateModalOpen(false);
                setNewPermission({
                  permissionName: '',
                  permissionCode: '',
                  resourceType: '',
                  action: '',
                  description: ''
                });
                setErrors({});
              }
            }}
          />
          <Dialog 
            open={isCreateModalOpen} 
            onOpenChange={(open) => {
              if (!open && !loading) {
                setIsCreateModalOpen(false);
                setNewPermission({
                  permissionName: '',
                  permissionCode: '',
                  resourceType: '',
                  action: '',
                  description: ''
                });
                setErrors({});
              }
            }}
          >
            <DialogContent 
              className="max-w-2xl max-h-[90vh] overflow-y-auto z-50"
              onClose={() => {
                if (!loading) {
                  setIsCreateModalOpen(false);
                  setNewPermission({
                    permissionName: '',
                    permissionCode: '',
                    resourceType: '',
                    action: '',
                    description: ''
                  });
                  setErrors({});
                }
              }}
            >
              <DialogHeader>
                <DialogTitle>Create New Permission</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="permissionName">Permission Name *</Label>
                  <Input
                    id="permissionName"
                    value={newPermission.permissionName}
                    onChange={(e) => setNewPermission({ ...newPermission, permissionName: e.target.value })}
                    placeholder="e.g., View Users"
                  />
                  {errors.permissionName && <p className="text-red-500 text-sm mt-1">{errors.permissionName}</p>}
                </div>
                <div>
                  <Label htmlFor="permissionCode">Permission Code *</Label>
                  <Input
                    id="permissionCode"
                    value={newPermission.permissionCode}
                    onChange={(e) => setNewPermission({ ...newPermission, permissionCode: e.target.value.toUpperCase() })}
                    placeholder="e.g., USERS_VIEW"
                  />
                  <p className="text-gray-500 text-xs mt-1">Uppercase letters, numbers, and underscores only</p>
                  {errors.permissionCode && <p className="text-red-500 text-sm mt-1">{errors.permissionCode}</p>}
                </div>
                <div>
                  <Label htmlFor="resourceType">Resource Type *</Label>
                  <Input
                    id="resourceType"
                    value={newPermission.resourceType}
                    onChange={(e) => setNewPermission({ ...newPermission, resourceType: e.target.value })}
                    placeholder="e.g., users, tickets, reports"
                  />
                  {errors.resourceType && <p className="text-red-500 text-sm mt-1">{errors.resourceType}</p>}
                </div>
                <div>
                  <Label htmlFor="action">Action *</Label>
                  <select
                    id="action"
                    value={newPermission.action}
                    onChange={(e) => setNewPermission({ ...newPermission, action: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Action</option>
                    <option value="read">Read (View)</option>
                    <option value="create">Create</option>
                    <option value="update">Update (Edit)</option>
                    <option value="delete">Delete</option>
                    <option value="manage">Manage (Full Access)</option>
                  </select>
                  {errors.action && <p className="text-red-500 text-sm mt-1">{errors.action}</p>}
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <textarea
                    id="description"
                    value={newPermission.description}
                    onChange={(e) => setNewPermission({ ...newPermission, description: e.target.value })}
                    placeholder="Permission description..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[100px]"
                    rows="4"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setNewPermission({
                        permissionName: '',
                        permissionCode: '',
                        resourceType: '',
                        action: '',
                        description: ''
                      });
                      setErrors({});
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleCreatePermission} disabled={loading}>
                    {loading ? 'Creating...' : 'Create Permission'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Edit Permission Modal */}
      {isEditModalOpen && selectedPermission && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => {
              setIsEditModalOpen(false);
              setSelectedPermission(null);
            }}
          />
          <Dialog 
            open={isEditModalOpen} 
            onOpenChange={(open) => {
              if (!open) {
                setIsEditModalOpen(false);
                setSelectedPermission(null);
              }
            }}
          >
            <DialogContent 
              className="max-w-2xl max-h-[90vh] overflow-y-auto z-50"
              onClose={() => {
                setIsEditModalOpen(false);
                setSelectedPermission(null);
              }}
            >
              <DialogHeader>
                <DialogTitle>Edit Permission</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-permissionName">Permission Name *</Label>
                  <Input
                    id="edit-permissionName"
                    value={newPermission.permissionName}
                    onChange={(e) => setNewPermission({ ...newPermission, permissionName: e.target.value })}
                  />
                  {errors.permissionName && <p className="text-red-500 text-sm mt-1">{errors.permissionName}</p>}
                </div>
                <div>
                  <Label htmlFor="edit-permissionCode">Permission Code *</Label>
                  <Input
                    id="edit-permissionCode"
                    value={newPermission.permissionCode}
                    onChange={(e) => setNewPermission({ ...newPermission, permissionCode: e.target.value.toUpperCase() })}
                  />
                  {errors.permissionCode && <p className="text-red-500 text-sm mt-1">{errors.permissionCode}</p>}
                </div>
                <div>
                  <Label htmlFor="edit-resourceType">Resource Type *</Label>
                  <Input
                    id="edit-resourceType"
                    value={newPermission.resourceType}
                    onChange={(e) => setNewPermission({ ...newPermission, resourceType: e.target.value })}
                  />
                  {errors.resourceType && <p className="text-red-500 text-sm mt-1">{errors.resourceType}</p>}
                </div>
                <div>
                  <Label htmlFor="edit-action">Action *</Label>
                  <select
                    id="edit-action"
                    value={newPermission.action}
                    onChange={(e) => setNewPermission({ ...newPermission, action: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Action</option>
                    <option value="read">Read (View)</option>
                    <option value="create">Create</option>
                    <option value="update">Update (Edit)</option>
                    <option value="delete">Delete</option>
                    <option value="manage">Manage (Full Access)</option>
                  </select>
                  {errors.action && <p className="text-red-500 text-sm mt-1">{errors.action}</p>}
                </div>
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <textarea
                    id="edit-description"
                    value={newPermission.description}
                    onChange={(e) => setNewPermission({ ...newPermission, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[100px]"
                    rows="4"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedPermission(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleUpdatePermission}>
                    Update Permission
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default PermissionManagement;

